import { createApp, reactive } from 'vue'
import FormConfirmSubmitDialog from './FormConfirmSubmitDialog'

const MOUNT_SELECTOR = '.kb-component--form-confirm-submit-dialog'
const MOUNT_CLASS = 'kb-component--form-confirm-submit-dialog'
const ROOT_SELECTOR = '[data-confirm-submit="true"]'
const FORM_SELECTOR = '[data-f-type="form"]'
const CONFIRM_PASSED_ATTR = 'data-confirm-passed'
const SUBMIT_BUTTON_SELECTOR =
    '.FormSubmitButton, [data-f-type="submitbutton"]'
const BTN_NEXT_CLASS = 'btnNext'
const BTN_PREV_CLASS = 'btnPrev'

const defaults = {
    title: 'Er du sikker på, at du vil fortryde dit køb?',
    text: 'Når du bekræfter, registrerer vi din fortrydelse og sender dig en kvittering på mail.',
    confirmLabel: 'Bekræft fortrydelse',
    cancelLabel: 'Annuller',
}

const dialogState = reactive({
    open: false,
    title: '',
    text: '',
    confirmLabel: '',
    cancelLabel: '',
    onConfirm: null,
    onCancel: null,
})

let appMounted = false
let listenersBound = false
let storeRef = null
let pendingSubmitter = null
let pendingForm = null
let pendingRoot = null

const isNavigationSubmitter = (submitter) =>
    !!submitter &&
    (submitter.classList.contains(BTN_NEXT_CLASS) ||
        submitter.classList.contains(BTN_PREV_CLASS) ||
        submitter.hasAttribute('data-f-navigation-next') ||
        submitter.hasAttribute('data-f-navigation-previous'))

const isFormSubmitButton = (el) => !!el?.matches?.(SUBMIT_BUTTON_SELECTOR)

const getFormFromRoot = (root) =>
    root.matches(FORM_SELECTOR) ? root : root.querySelector(FORM_SELECTOR)

const getSubmitButton = (form, preferred) => {
    if (
        preferred &&
        form.contains(preferred) &&
        isFormSubmitButton(preferred)
    ) {
        return preferred
    }
    return form.querySelector(SUBMIT_BUTTON_SELECTOR)
}

const closeDialog = () => {
    dialogState.open = false
    dialogState.onConfirm = null
    dialogState.onCancel = null
    pendingSubmitter = null
    pendingForm = null
    pendingRoot = null
}

const ensureMounted = () => {
    if (appMounted || !storeRef) return

    let mountEl = document.querySelector(MOUNT_SELECTOR)
    if (!mountEl) {
        mountEl = document.createElement('div')
        mountEl.className = MOUNT_CLASS
        document.body.appendChild(mountEl)
    }

    createApp(FormConfirmSubmitDialog, { dialogState }).use(storeRef).mount(mountEl)
    appMounted = true
}

const openDialog = ({ root, form, submitter }) => {
    ensureMounted()
    pendingRoot = root
    pendingForm = form
    pendingSubmitter = submitter || null

    dialogState.title = root.dataset.confirmTitle || defaults.title
    dialogState.text = root.dataset.confirmText || defaults.text
    dialogState.confirmLabel =
        root.dataset.confirmConfirmLabel || defaults.confirmLabel
    dialogState.cancelLabel =
        root.dataset.confirmCancelLabel || defaults.cancelLabel

    dialogState.onCancel = closeDialog
    dialogState.onConfirm = () => {
        const formToSubmit = pendingForm
        const submitterToUse = pendingSubmitter
        const rootEl = pendingRoot
        closeDialog()
        if (!formToSubmit || !rootEl) return

        const button = getSubmitButton(formToSubmit, submitterToUse)
        if (!button) return

        rootEl.setAttribute(CONFIRM_PASSED_ATTR, 'true')
        try {
            button.click()
        } finally {
            window.setTimeout(() => {
                rootEl.removeAttribute(CONFIRM_PASSED_ATTR)
            }, 0)
        }
    }

    dialogState.open = true
}

const shouldOpenDialog = (root) =>
    root?.matches?.(ROOT_SELECTOR) &&
    root.getAttribute(CONFIRM_PASSED_ATTR) !== 'true'

const onDocumentSubmit = (event) => {
    const root = event.target?.closest?.(ROOT_SELECTOR)
    if (!root?.matches?.(ROOT_SELECTOR)) return
    if (isNavigationSubmitter(event.submitter)) return

    event.preventDefault()
    event.stopImmediatePropagation()

    if (!shouldOpenDialog(root)) return

    const form = getFormFromRoot(root)
    if (!form) return
    openDialog({ root, form, submitter: event.submitter })
}

const onDocumentClick = (event) => {
    const submitter = event.target?.closest?.(
        'button, input[type="submit"], input[type="image"]'
    )
    if (!submitter || isNavigationSubmitter(submitter)) return
    if (!isFormSubmitButton(submitter)) return

    const root = submitter.closest(ROOT_SELECTOR)
    if (!shouldOpenDialog(root)) return

    const form = getFormFromRoot(root)
    if (!form) return

    event.preventDefault()
    event.stopImmediatePropagation()
    openDialog({ root, form, submitter })
}

export default function (Store) {
    storeRef = Store

    if (!listenersBound) {
        document.addEventListener('submit', onDocumentSubmit, true)
        document.addEventListener('click', onDocumentClick, true)
        listenersBound = true
    }

    if (document.querySelector(ROOT_SELECTOR) || document.querySelector(MOUNT_SELECTOR)) {
        ensureMounted()
    }
}
